// FeedbackDialog.tsx — the rail's "Feedback" trigger + its modal (THI-92, spec
// docs/superpowers/specs/2026-08-18-feedback-form-design.md "Dashboard — user side"). Placement
// is the rail's bottom zone (workspace-level, beside the account row), not the bot-scoped nav —
// feedback isn't about the current bot, it's about Embent. Same house rule as every other
// bottom-zone control (RailNav.tsx:66-71): one component, a `touch` prop for the drawer's larger
// target, never a fork.
//
// `FeedbackForm` is keyed by `formInstance` (bumped on every close) rather than reset via an
// effect: closing the dialog unmounts the form subtree, which both clears its state (type,
// message, success) AND runs the pending-timer cleanup — one mechanism for both requirements
// instead of two. `DialogContent` itself is NOT part of that remount, so Radix's own close
// animation (data-[state=closed] on DialogPrimitive.Content, dialog.tsx) still plays undisturbed.
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import { useLocation } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import type { FeedbackCreateInput } from '@repo/validation';
import type { FeedbackDto, FeedbackType } from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/components/dialog';
import { apiFetch, ApiError } from '../../lib/api';
import type { Catalog } from '../../locales/en';
import { useT } from '../../locales/context';

/** How long the "Sent" state stays up before the dialog closes itself. */
const AUTO_CLOSE_MS = 1500;

/** `/bots/:id` is the only place a bot id appears in the dashboard's URL shape (`RailNav.tsx`
 * builds every bot-scoped link off the same prefix) — anywhere else, there's no bot in view and
 * `context.botId` is omitted per `feedbackCreateSchema`. */
const BOT_ID_PATTERN = /\/bots\/([^/]+)/;

/** Locale-dashboard plan, Task 4: option labels moved to `t.shell.*` keys — built from `t`
 * rather than a module-level constant. */
function typeOptionsFor(t: Catalog): { value: FeedbackType; label: string }[] {
  return [
    { value: 'bug', label: t.shell.feedbackTypeBug },
    { value: 'feature', label: t.shell.feedbackTypeFeature },
    { value: 'question', label: t.shell.feedbackTypeQuestion },
  ];
}

function TypeToggle({ value, onChange }: { value: FeedbackType | null; onChange: (next: FeedbackType) => void }) {
  const t = useT();
  const typeOptions = typeOptionsFor(t);
  return (
    <div role="group" aria-label={t.shell.feedbackTypeGroupLabel} className="flex gap-2">
      {typeOptions.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
          className={`flex-1 rounded-md border px-2.5 py-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35 ${
            value === opt.value
              ? 'border-primary bg-[color-mix(in_oklch,var(--primary)_var(--st-wash),transparent)] text-foreground'
              : 'border-input text-muted-foreground hover:bg-accent'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** The dialog's body — form while composing, thank-you once sent. Unmounted (via the parent's
 * `formInstance` key) every time the dialog closes, which is what resets its state and cancels
 * its auto-close timer; see the file-level comment. */
function FeedbackForm({ onSent }: { onSent: () => void }) {
  const location = useLocation();
  const t = useT();
  const [type, setType] = useState<FeedbackType | null>(null);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const submitFeedback = useMutation({
    mutationFn: (payload: FeedbackCreateInput) =>
      apiFetch<FeedbackDto>('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      setSubmitted(true);
      closeTimerRef.current = setTimeout(onSent, AUTO_CLOSE_MS);
    },
  });

  const canSubmit = type !== null && message.trim().length > 0;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit || submitFeedback.isPending) return;
    const botId = BOT_ID_PATTERN.exec(location.pathname)?.[1];
    submitFeedback.mutate({
      type,
      message,
      context: botId ? { path: location.pathname, botId } : { path: location.pathname },
    });
  }

  if (submitted) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{t.shell.feedbackSent}</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <TypeToggle value={type} onChange={setType} />
      <div className="flex flex-col gap-1">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={5000}
          rows={5}
          placeholder={t.shell.feedbackPlaceholder}
          className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
        />
        <span className="self-end text-xs text-muted-foreground">{message.length} / 5000</span>
      </div>
      {submitFeedback.isError && (
        <p className="text-[13px] text-destructive">
          {submitFeedback.error instanceof ApiError ? submitFeedback.error.message : t.shell.feedbackSendError}
        </p>
      )}
      <DialogFooter>
        <Button type="submit" disabled={!canSubmit || submitFeedback.isPending}>
          {submitFeedback.isPending ? t.shell.feedbackSending : t.shell.feedbackSend}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** The rail's "Feedback" row + the dialog it owns. Same visual register as the rail's
 * workspace+account menu trigger (icon + label, `WorkspaceMenu.tsx`'s row height/padding), and
 * the same `touch` prop for the drawer's larger target — see the file-level comment for why
 * this is one component, not a fork. */
export function FeedbackTrigger({ touch = false }: { touch?: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [formInstance, setFormInstance] = useState(0);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    // Bump only on close — see the file-level comment on why this is the reset mechanism.
    if (!next) setFormInstance((n) => n + 1);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={`flex w-full shrink-0 items-center rounded-[9px] text-left text-muted-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35 ${
            touch ? 'min-h-11 gap-2.5 p-2 text-[13px]' : 'h-[42px] gap-[9px] px-2 text-[12.5px]'
          }`}
        >
          <MessageSquarePlus size={touch ? 17 : 16} strokeWidth={1.9} className="shrink-0" />
          <span className="flex-1 truncate">{t.shell.feedbackTrigger}</span>
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.shell.feedbackDialogTitle}</DialogTitle>
          <DialogDescription>{t.shell.feedbackDialogDescription}</DialogDescription>
        </DialogHeader>
        <FeedbackForm key={formInstance} onSent={() => handleOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
